import Button from "../../../shared/components/Button";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type { ParticipantCheckin } from "../services/eventRegistrationAdminService";

export type MatchCheckInSide = {
  id: string;
  label: string;
  matchId?: number | null;
  participantCpfs: string[];
  participants?: Array<{
    cpf: string;
    label: string;
  }>;
};

type AdminMatchCheckInControlProps = {
  canEdit?: boolean;
  checkins?: ParticipantCheckin[];
  onToggleSide?: (side: MatchCheckInSide, checkedIn: boolean) => void;
  pendingSideId?: string | null;
  showReadinessStatus?: boolean;
  sides: [MatchCheckInSide, MatchCheckInSide];
};

function normalizeCpf(value: string | null | undefined) {
  return String(value || "").replace(/\D/g, "").slice(0, 11);
}

function uniqueCpfs(values: string[]) {
  return Array.from(
    new Set(values.map(normalizeCpf).filter((cpf) => cpf.length >= 8)),
  );
}

function buildCheckinMap(checkins: ParticipantCheckin[] | undefined) {
  const map = new Map<string, ParticipantCheckin>();
  (Array.isArray(checkins) ? checkins : []).forEach((item) => {
    const cpf = normalizeCpf(item?.cpf);
    if (!cpf) return;
    const matchId = Number(item?.matchId || 0);
    map.set(matchId > 0 ? `${matchId}:${cpf}` : cpf, item);
  });
  return map;
}

export function sideIsPresent(
  side: MatchCheckInSide,
  checkins: ParticipantCheckin[] | undefined,
) {
  const cpfs = uniqueCpfs(side.participantCpfs);
  if (cpfs.length === 0) return false;
  const checkinMap = buildCheckinMap(checkins);
  const matchId = Number(side.matchId || 0);
  return cpfs.every((cpf) => {
    const key = matchId > 0 ? `${matchId}:${cpf}` : cpf;
    return Boolean(checkinMap.get(key)?.checkedIn);
  });
}

function sideHasParticipants(side: MatchCheckInSide) {
  return uniqueCpfs(side.participantCpfs).length > 0;
}

function sideParticipantRows(side: MatchCheckInSide) {
  return (side.participants || [])
    .map((participant) => ({
      cpf: normalizeCpf(participant.cpf),
      label: String(participant.label || "").trim(),
    }))
    .filter((participant) => participant.cpf);
}

function resolveReadinessLabel(
  sides: [MatchCheckInSide, MatchCheckInSide],
  checkins: ParticipantCheckin[] | undefined,
) {
  const t = siteTexts.challengeEngine.checkIn;
  const hasBothSides = sides.every(sideHasParticipants);
  if (!hasBothSides) return t.waitingParticipants;
  const presentCount = sides.filter((side) => sideIsPresent(side, checkins)).length;
  if (presentCount === 2) return t.readyToCall;
  if (presentCount === 1) return t.waitingOpponents;
  return t.waitingParticipants;
}

function readinessClass(
  sides: [MatchCheckInSide, MatchCheckInSide],
  checkins: ParticipantCheckin[] | undefined,
) {
  const hasBothSides = sides.every(sideHasParticipants);
  if (!hasBothSides) return "is-waiting";
  const presentCount = sides.filter((side) => sideIsPresent(side, checkins)).length;
  if (presentCount === 2) return "is-ready";
  if (presentCount === 1) return "is-partial";
  return "is-waiting";
}

export default function AdminMatchCheckInControl({
  canEdit = false,
  checkins,
  onToggleSide,
  pendingSideId = null,
  showReadinessStatus = true,
  sides,
}: AdminMatchCheckInControlProps) {
  const t = siteTexts.challengeEngine.checkIn;
  const visibleSides = sides.filter(sideHasParticipants);
  const checkinMap = buildCheckinMap(checkins);
  if (visibleSides.length === 0) return null;

  return (
    <div className="admin-match-checkin">
      {showReadinessStatus && (
        <div
          className={`admin-match-checkin-status ${readinessClass(
            sides,
            checkins,
          )}`}
        >
          {resolveReadinessLabel(sides, checkins)}
        </div>
      )}
      <div className="admin-match-checkin-sides">
        {visibleSides.map((side) => {
          const present = sideIsPresent(side, checkins);
          const pending = pendingSideId === side.id;
          const participantRows = sideParticipantRows(side);
          const matchId = Number(side.matchId || 0);
          return (
            <div key={side.id} className="admin-match-checkin-side">
              <span className="admin-match-checkin-side-label">
                {side.label}
              </span>
              <strong className={present ? "is-present" : "is-waiting"}>
                {present ? t.present : t.waitingArrival}
              </strong>
              {participantRows.length > 0 && (
                <div className="admin-match-checkin-participants">
                  {participantRows.map((participant) => {
                  const participantPresent = Boolean(
                    checkinMap.get(
                      matchId > 0
                        ? `${matchId}:${participant.cpf}`
                        : participant.cpf,
                    )?.checkedIn,
                  );
                  return (
                    <span
                      key={`${side.id}-${participant.cpf}`}
                      className={`admin-match-checkin-participant ${
                        participantPresent ? "is-present" : "is-waiting"
                      }`}
                    >
                      <span>{participant.label}</span>
                      <strong>
                        {participantPresent ? t.present : t.waitingArrival}
                      </strong>
                    </span>
                  );
                  })}
                </div>
              )}
              {canEdit && onToggleSide && (
                <Button
                  type="button"
                  variant="pill"
                  disabled={pending}
                  onClick={() => onToggleSide(side, !present)}
                >
                  {present ? t.undo : t.confirm}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
